/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  memo,
  MouseEventHandler,
  useCallback,
  useImperativeHandle,
  useState
} from 'react';
import {Typography} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {isValidEmail} from '@ion/core/src/utils/Validators';
import {ChipTextField} from '@ion/dls/src/components/textfield/ChipTextField';
import styles from '@ion/dls/src/editors/editorStyles';
import EmbeddedForm from '@ion/dls/src/editors/forms/EmbeddedForm';
import {EmailExportFields} from '@ion/dls/src/export/EmailExportRequestUtils';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {FormState, useFormState, validate} from '@ion/dls/src/hooks/useFormState';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface DevicePasswordFormProps {
  handleActionChooser?: MouseEventHandler;
  onSave: (details: GenericObject) => void;
};

const useStyles = (theme: DLSTheme) => ({
  formTemplate : {
    marginTop : '20px'
  }
});

export const DevicePasswordForm = forwardRef(({onSave}: DevicePasswordFormProps, ref) => {
  const maxInputEmailLength                 = 7;
  const dialogStyles                        = useExtendStyles(styles, useStyles);
  const classes                             = dialogStyles();
  const [maxLengthError, setMaxLengthError] = useState<boolean>(false);
  const [formState, setFormState]           = useFormState({[EmailExportFields.EmailAddress]: {required: true}});

  const onBeforeAdd = useCallback((value: string): boolean => {
    const validate = !formState[EmailExportFields.EmailAddress]?.value ||
    (formState[EmailExportFields.EmailAddress]?.value?.length < maxInputEmailLength);
    setMaxLengthError(value && !validate);
    return validate;
  }, [formState]);

  const onSubmit = useFormSave({
    formState,
    serialize,
    setFormState,
    onSave
  });

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  const validateEmail = useCallback((value) => {
    const items = [];
    value?.forEach(item => {
      const error = item && !validate(item, isValidEmail);
      items.push(error);
    });
    return items;
  }, []);

  const handleFieldChange = useCallback((value) => {
    const name  = EmailExportFields.EmailAddress;
    const error =  validateEmail(value);
    setFormState({
      [name] : {
        name,
        value,
        required : false,
        error    : error?.some((item) => item === true)
      }
    });
  }, [formState]);

  const onUpdateInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const {name, value} = e?.target;
    const error = value && !validate(value, isValidEmail);
    if (!value) setMaxLengthError(false);
    setFormState({
      [name] : {
        ...formState[name],
        error
      }
    });
  }, [formState]);

  return (
    <>
      <EmbeddedForm>
        <Typography component='h1' variant='h2'>
          {translate('admin-devices:devicePassword.grid.title')}
        </Typography>
        <div className={classes.formTemplate}>
          {translate('common:export.emailExporting.label')}
        </div>
        <div className={classes.formTemplate}>
          <ChipTextField name={EmailExportFields.EmailAddress}
            onUpdateInput={onUpdateInput}
            onChange={handleFieldChange}
            onBeforeAdd={onBeforeAdd}
            blurBehavior={'add'}
            fieldLabel={translate('common:editors.emailAddress.label')}
            placeholder={translate('common:editors.emailAddress.placeholder')}
            hasError={formState[EmailExportFields.EmailAddress]?.error || maxLengthError}
            errorText={maxLengthError
              ? translate('common:export.emailExporting.maxInputLengthError')
              : translate('common:editors.emailAddress.error')}/>
        </div>
        <div className={classes.formTemplate}>
          {translate('admin-devices:devicePassword.dialog.description')}
        </div>
      </EmbeddedForm>
    </>
  );
});

const serialize = (formState: FormState) => (
  formState[EmailExportFields.EmailAddress]?.value
);

export default memo(DevicePasswordForm);
